import styled, { keyframes } from 'styled-components';

export const Container = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  padding: 12px var(--cco-pad) 14px;
  gap: 10px;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;

  @media (max-width: 720px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const HeaderText = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 5px 12px;
  border-radius: 999px;
  background: var(--cco-blue-soft);
  color: var(--cco-blue);
  box-shadow: inset 0 0 0 1px rgba(78, 161, 243, 0.25);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

export const Hint = styled.span`
  min-width: 0;
  overflow: hidden;
  color: var(--cco-ink-mute);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  gap: 7px;
  min-height: 30px;
  padding: 0 11px;
  border: 1px solid var(--cco-line);
  border-radius: 8px;
  background: transparent;
  color: var(--cco-cyan);
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  :hover:not(:disabled) {
    background: rgba(28, 176, 216, 0.1);
    border-color: rgba(28, 176, 216, 0.35);
  }

  :disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const FilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 27px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid
    ${(props) => (props.$active ? 'var(--cco-line-strong)' : 'var(--cco-line)')};
  background: ${(props) =>
    props.$active ? 'var(--cco-soft-surface)' : 'transparent'};
  color: ${(props) =>
    props.$active ? 'var(--cco-ink)' : 'var(--cco-ink-faint)'};
  font: inherit;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;

  .n {
    color: ${(props) =>
      props.$active ? 'var(--cco-ink)' : 'var(--cco-ink-mute)'};
    font-variant-numeric: tabular-nums;
  }

  :disabled {
    opacity: 0.42;
    cursor: default;
  }
`;

export const LogList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid var(--cco-line);
  border-radius: 8px;
`;

export const LogItem = styled.div`
  display: grid;
  grid-template-columns:
    minmax(82px, auto) minmax(160px, 1fr) minmax(86px, 0.45fr)
    minmax(86px, 0.45fr) minmax(114px, auto);
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 10px 12px;
  border-bottom: 1px solid var(--cco-line);

  :last-child {
    border-bottom: 0;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    align-items: flex-start;
    gap: 5px;
  }
`;

export const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  justify-self: flex-start;
  min-width: 82px;
  height: 24px;
  padding: 0 9px;
  border: 1px solid
    ${(props) =>
      props.$type === 'setpoint'
        ? 'rgba(28, 176, 216, 0.5)'
        : props.$type === 'command'
        ? 'rgba(43, 212, 125, 0.45)'
        : 'rgba(245, 184, 73, 0.5)'};
  border-radius: 999px;
  color: ${(props) =>
    props.$type === 'setpoint'
      ? 'var(--cco-cyan)'
      : props.$type === 'command'
      ? 'var(--cco-green)'
      : 'var(--cco-yellow, #f5b849)'};
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const MainCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const ItemName = styled.span`
  min-width: 0;
  overflow: hidden;
  color: var(--cco-ink);
  font-size: 13px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const UserEmail = styled.span`
  min-width: 0;
  overflow: hidden;
  color: var(--cco-ink-faint);
  font-size: 11px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ValueCell = styled.span`
  min-width: 0;
  overflow: hidden;
  color: ${(props) =>
    props.$tone === 'current'
      ? 'var(--cco-green)'
      : props.$tone === 'previous'
      ? 'var(--cco-yellow, #f5b849)'
      : 'var(--cco-ink-mute)'};
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    'Liberation Mono', monospace;
  font-size: 11px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 900px) {
    ::before {
      content: ${(props) =>
        props.$tone === 'current' ? "'Atual: '" : "'Anterior: '"};
      color: var(--cco-ink-faint);
      font-family: inherit;
      font-weight: 700;
    }
  }
`;

export const DateCell = styled.span`
  justify-self: flex-end;
  color: var(--cco-ink-mute);
  font-size: 11.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  @media (max-width: 900px) {
    justify-self: flex-start;
  }
`;

export const State = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 86px;
  border: 1px solid var(--cco-line);
  border-radius: 8px;
  color: var(--cco-ink-mute);
  font-size: 13px;
  text-align: center;
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

export const Skeleton = styled.div`
  height: 14px;
  width: ${(props) => props.$w || '70%'};
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 0%,
    rgba(255, 255, 255, 0.08) 50%,
    rgba(255, 255, 255, 0.04) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;
